import { useState, useEffect, useCallback, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import QRCode from 'qrcode';
import { EntranceDirectionIndicator } from '../components/legacy-ball/table-management/EntranceDirectionIndicator';
import { MiniFloorplanMap } from '../components/legacy-ball/table-management/MiniFloorplanMap';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BG_PAGE,
  BG_CARD,
  BORDER_LIGHT,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';
import { API_BASE } from '../api/client';

interface GuestDirectoryResult {
  fullName: string;
  ticketNumber: string | null;
  tableNumber: number | null;
  tableName: string | null;
  seatNumber: number | null;
  zoneName: string | null;
  chapterAffiliation: string | null;
  orderReferenceId: string | null;
  registrationId?: string | null;
  isCheckedIn?: boolean;
  checkedInAt?: string | null;
  pickupLocation?: string | null;
  directions?: string | null;
  suggestedEntrance?: string | null;
}

interface GuestCardData {
  fullName: string;
  ticketNumber: string;
  registrationId: string | null;
  tierName: string | null;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  zoneName: string | null;
  entryGuidance: string | null;
  orderReferenceId: string | null;
  chapterAffiliation: string | null;
  mealSelection: string | null;
  isCheckedIn: boolean;
  checkedInAt: string | null;
  suggestedEntrance: string | null;
  entranceDirection: string | null;
  gridAddress: string | null;
  wayfindingInstructions: string | null;
}

const CACHE_KEY = 'kotoku_guest_data';
const CACHE_TS_KEY = 'kotoku_guest_data_ts';
const OFFLINE_MODE_KEY = 'kotoku_force_offline';

function getCachedData(): GuestDirectoryResult[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getCacheTimestamp(): string | null {
  return localStorage.getItem(CACHE_TS_KEY);
}

function getForceOffline(): boolean {
  return localStorage.getItem(OFFLINE_MODE_KEY) === 'true';
}

function localSearch(data: GuestDirectoryResult[], query: string): GuestDirectoryResult[] {
  const q = query.toLowerCase().trim();
  if (q.length < 2) return [];
  return data.filter((g) =>
    (g.fullName || '').toLowerCase().includes(q) ||
    (g.ticketNumber || '').toLowerCase().includes(q) ||
    (g.orderReferenceId || '').toLowerCase().includes(q) ||
    (g.chapterAffiliation || '').toLowerCase().includes(q) ||
    (g.tableName || '').toLowerCase().includes(q)
  ).slice(0, 30);
}

export function GuestDirectoryPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GuestDirectoryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cachedCount, setCachedCount] = useState(0);
  const [cacheTime, setCacheTime] = useState<string | null>(null);
  const [forceOffline, setForceOffline] = useState(getForceOffline());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [checkInStatus, setCheckInStatus] = useState<Record<string, { loading: boolean; message: string | null }>>({});
  const [checkinPin, setCheckinPin] = useState(() => {
    try { return localStorage.getItem('kotoku_checkin_pin') || ''; } catch { return ''; }
  });

  // Guest card detail state
  const [selectedCard, setSelectedCard] = useState<GuestCardData | null>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);

  // Load cache info on mount + register service worker
  useEffect(() => {
    // Set page title for home screen bookmark
    document.title = 'Kotoku Mobile';

    const cached = getCachedData();
    if (cached) setCachedCount(cached.length);
    setCacheTime(getCacheTimestamp());

    // Register Kotoku service worker for offline page caching
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/kotoku-sw.js').catch(() => {});
    }

    // Inject PWA manifest link if not already present
    if (!document.querySelector('link[rel="manifest"][href*="kotoku"]')) {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/kotoku-manifest.json';
      document.head.appendChild(link);
    }

    // Set apple-mobile-web-app meta tags for iOS home screen
    if (!document.querySelector('meta[name="apple-mobile-web-app-title"]')) {
      const m = document.createElement('meta');
      m.name = 'apple-mobile-web-app-title';
      m.content = 'Kotoku Mobile';
      document.head.appendChild(m);
    }
    if (!document.querySelector('meta[name="apple-mobile-web-app-capable"]')) {
      const m = document.createElement('meta');
      m.name = 'apple-mobile-web-app-capable';
      m.content = 'yes';
      document.head.appendChild(m);
    }

    // Set theme-color meta
    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = '#1a6b3c';

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const useOffline = forceOffline || !isOnline;

  // Sync data for offline use
  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      const response = await fetch(`${API_BASE}/guest-directory/all`);
      if (!response.ok) throw new Error('Sync failed');
      const data: GuestDirectoryResult[] = await response.json();
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      const ts = new Date().toLocaleString();
      localStorage.setItem(CACHE_TS_KEY, ts);
      setCachedCount(data.length);
      setCacheTime(ts);
    } catch (err: any) {
      setSyncError(err?.message || 'Failed to sync data');
    } finally {
      setSyncing(false);
    }
  }, []);

  // Toggle force offline
  const toggleForceOffline = () => {
    const next = !forceOffline;
    setForceOffline(next);
    localStorage.setItem(OFFLINE_MODE_KEY, String(next));
  };

  // QR Scanner — uses file input (bypasses camera permission)
  const handleScanFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanError(null);
    setScanning(true);
    try {
      const scanner = new Html5Qrcode('kotoku-qr-file-reader');
      const decodedText = await scanner.scanFile(file, true);
      await scanner.clear();
      setScanning(false);
      handleQrResult(decodedText);
    } catch {
      setScanError('Could not read QR code from image');
      setScanning(false);
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleQrResult = (decodedText: string) => {
    setSearched(true);
    // QR encodes JSON like { type: 'guest', identifier: registrationId }
    let registrationId: string | null = null;
    try {
      const parsed = JSON.parse(decodedText);
      if (parsed.identifier) registrationId = parsed.identifier;
    } catch {
      // Might be a plain ticket number or text
      registrationId = null;
    }

    if (registrationId) {
      // Look up from cached data by registrationId
      const cached = getCachedData();
      if (cached) {
        const match = cached.filter((g) => g.registrationId === registrationId);
        if (match.length > 0) {
          setResults(match);
          setQuery(match[0].fullName);
          return;
        }
      }
      // If not in cache, try by text (the decoded text might be a name or ticket)
      setQuery(registrationId);
    } else {
      // Plain text QR — use as search query
      setQuery(decodedText);
    }
    // Trigger a search with whatever we got
    const cached = getCachedData();
    if (cached) {
      const q = (registrationId || decodedText).toLowerCase().trim();
      const match = cached.filter((g) =>
        (g.fullName || '').toLowerCase().includes(q) ||
        (g.ticketNumber || '').toLowerCase().includes(q) ||
        (g.orderReferenceId || '').toLowerCase().includes(q)
      );
      setResults(match.slice(0, 30));
    }
  };

  // Check-in handler (online mode only)
  const handleCheckIn = useCallback(async (registrationId: string) => {
    if (!registrationId || useOffline) return;
    if (!checkinPin) {
      const pin = prompt('Enter check-in PIN:');
      if (!pin) return;
      setCheckinPin(pin);
      localStorage.setItem('kotoku_checkin_pin', pin);
    }
    const pin = checkinPin || localStorage.getItem('kotoku_checkin_pin') || '';
    setCheckInStatus((prev) => ({ ...prev, [registrationId]: { loading: true, message: null } }));
    try {
      const response = await fetch(`${API_BASE}/guest-directory/check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registrationId, pickupLocation: 'Ball_Door', pin }),
      });
      const result = await response.json();
      setCheckInStatus((prev) => ({ ...prev, [registrationId]: { loading: false, message: result.message } }));
    } catch {
      setCheckInStatus((prev) => ({ ...prev, [registrationId]: { loading: false, message: 'Check-in failed' } }));
    }
  }, [useOffline, checkinPin]);

  // Undo check-in handler (online mode only)
  const handleUndoCheckIn = useCallback(async (registrationId: string) => {
    if (!registrationId || useOffline) return;
    const pin = checkinPin || localStorage.getItem('kotoku_checkin_pin') || '';
    if (!pin) {
      const entered = prompt('Enter check-in PIN:');
      if (!entered) return;
      setCheckinPin(entered);
      localStorage.setItem('kotoku_checkin_pin', entered);
    }
    const finalPin = pin || checkinPin || localStorage.getItem('kotoku_checkin_pin') || '';
    setCheckInStatus((prev) => ({ ...prev, [registrationId]: { loading: true, message: null } }));
    try {
      const response = await fetch(`${API_BASE}/guest-directory/undo-check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registrationId, pin: finalPin }),
      });
      const result = await response.json();
      setCheckInStatus((prev) => ({ ...prev, [registrationId]: { loading: false, message: result.message } }));
      // Update the local results to reflect the undo
      if (result.success) {
        setResults((prev) => prev.map((g) =>
          g.registrationId === registrationId ? { ...g, isCheckedIn: false, checkedInAt: null, pickupLocation: null } : g
        ));
      }
    } catch {
      setCheckInStatus((prev) => ({ ...prev, [registrationId]: { loading: false, message: 'Undo failed' } }));
    }
  }, [useOffline, checkinPin]);

  // Guest card detail handler — fetch full info when guest is clicked
  const handleGuestClick = useCallback(async (guest: GuestDirectoryResult) => {
    setCardLoading(true);
    setCardError(null);
    setSelectedCard(null);
    setShowMap(false);

    // If online and guest has a ticket number, try API first
    if (!useOffline && guest.ticketNumber) {
      try {
        const response = await fetch(`${API_BASE}/guest-card/${encodeURIComponent(guest.ticketNumber)}`);
        if (response.ok) {
          const cardData: GuestCardData = await response.json();
          setSelectedCard(cardData);
          setCardLoading(false);
          return;
        }
      } catch {
        // Fall through to offline card
      }
    }

    // Offline fallback — build card from the directory result data
    const offlineCard: GuestCardData = {
      fullName: guest.fullName,
      ticketNumber: guest.ticketNumber || '—',
      registrationId: guest.registrationId || null,
      tierName: null,
      tableName: guest.tableName,
      tableNumber: guest.tableNumber,
      seatNumber: guest.seatNumber,
      zoneName: guest.zoneName,
      entryGuidance: null,
      orderReferenceId: guest.orderReferenceId,
      chapterAffiliation: guest.chapterAffiliation,
      mealSelection: null,
      isCheckedIn: guest.isCheckedIn || false,
      checkedInAt: guest.checkedInAt || null,
      suggestedEntrance: guest.suggestedEntrance || null,
      entranceDirection: null,
      gridAddress: null,
      wayfindingInstructions: guest.directions || null,
    };
    setSelectedCard(offlineCard);
    setCardLoading(false);
  }, [useOffline]);

  const handleBackToResults = () => {
    setSelectedCard(null);
    setCardError(null);
    setShowMap(false);
  };

  // Search handler
  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setLoading(true);
    setSearched(true);

    if (useOffline) {
      // Search locally cached data
      const cached = getCachedData();
      if (cached) {
        setResults(localSearch(cached, trimmed));
      } else {
        setResults([]);
      }
      setLoading(false);
    } else {
      // Search via API
      try {
        const response = await fetch(`${API_BASE}/guest-directory/search?q=${encodeURIComponent(trimmed)}`);
        if (!response.ok) throw new Error();
        const data: GuestDirectoryResult[] = await response.json();
        setResults(data);
      } catch {
        // Fallback to local if API fails
        const cached = getCachedData();
        if (cached) {
          setResults(localSearch(cached, trimmed));
        } else {
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    }
  }, [query, useOffline]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const modeLabel = useOffline
    ? (forceOffline ? 'OFFLINE (forced)' : 'OFFLINE')
    : 'ONLINE';
  const modeColor = useOffline ? '#e65100' : '#2e7d32';

  // If a guest card is selected, show the detail view
  if (selectedCard) {
    return (
      <div style={styles.page}>
        <div style={{ width: '100%', maxWidth: '420px', margin: '0 auto', padding: '16px' }}>
          <button
            onClick={handleBackToResults}
            style={{ background: 'none', border: 'none', color: PRIMARY, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}
          >
            ← Back to results
          </button>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            {/* Title bar with Order # */}
            <div style={{ padding: '14px 20px', backgroundColor: '#1a6b3c', color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>
              REF/ORDER | {selectedCard.orderReferenceId || selectedCard.ticketNumber}
            </div>

            {/* Content area */}
            <div style={{ padding: '20px' }}>
              {/* Row 1: Name (left) + Tier (right) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f2d1f', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{selectedCard.fullName}</div>
                {selectedCard.tierName && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7b1fa2', backgroundColor: '#f3e5f5', padding: '3px 10px', borderRadius: '10px' }}>{selectedCard.tierName}</span>}
              </div>

              {/* Row 2: Chapter (left) + Zone (right) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.85rem', color: '#333' }}>Chapter: {selectedCard.chapterAffiliation || '—'}</span>
                {selectedCard.zoneName && <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1565c0' }}>Zone: {selectedCard.zoneName}</span>}
              </div>

              {/* QR Code */}
              {selectedCard.registrationId && (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <QrCodeDisplay registrationId={selectedCard.registrationId} />
                </div>
              )}

              {/* Separator */}
              <div style={{ height: '16px' }} />
              <hr style={{ border: 'none', borderTop: '2px solid #e5e7eb', margin: '0 0 16px' }} />

              {/* Table Name */}
              {(selectedCard.tableName || selectedCard.tableNumber != null) && (
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f2d1f', marginBottom: '10px' }}>
                  {selectedCard.tableName || `Table ${selectedCard.tableNumber}`}
                </div>
              )}

              {/* Table # pill + Seat # pill + Ticket # pill */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
                {selectedCard.tableNumber != null && (
                  <span style={{ padding: '12px 22px', borderRadius: '16px', backgroundColor: '#1a6b3c', color: '#fff', textAlign: 'center', lineHeight: 1.2 }}>
                    <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 400 }}>Table</span>
                    <span style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800 }}>{selectedCard.tableNumber}</span>
                  </span>
                )}
                {selectedCard.seatNumber != null && (
                  <span style={{ padding: '12px 22px', borderRadius: '16px', backgroundColor: '#0d47a1', color: '#fff', textAlign: 'center', lineHeight: 1.2 }}>
                    <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 400 }}>Seat</span>
                    <span style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800 }}>{selectedCard.seatNumber}</span>
                  </span>
                )}
                <span style={{ padding: '12px 22px', borderRadius: '16px', backgroundColor: '#7b1fa2', color: '#fff', textAlign: 'center', lineHeight: 1.2, flex: 1, minWidth: '100px' }}>
                  <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 400 }}>Ticket</span>
                  <span style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800 }}>{selectedCard.ticketNumber}</span>
                </span>
              </div>

              {/* Check-in status */}
              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: selectedCard.isCheckedIn ? '#16a34a' : '#d97706' }}>
                {selectedCard.isCheckedIn ? '✅ Checked in' : '⬜ Not checked in yet'}
              </div>

              {/* Wayfinding */}
              {(selectedCard.wayfindingInstructions || selectedCard.suggestedEntrance) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px' }}>
                  <span style={{ fontSize: '1rem', flexShrink: 0 }}>🧭</span>
                  <div>
                    {selectedCard.wayfindingInstructions ? (
                      <div style={{ fontSize: '0.82rem', color: '#1e40af', lineHeight: 1.5 }}>{selectedCard.wayfindingInstructions}</div>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>Enter via: {selectedCard.suggestedEntrance}</div>
                        {selectedCard.entranceDirection && (
                          <div style={{ fontSize: '0.78rem', color: '#3b82f6', marginTop: '2px' }}>📍 {selectedCard.entranceDirection}</div>
                        )}
                      </>
                    )}
                    <div style={{ marginTop: '8px' }}>
                      <EntranceDirectionIndicator suggestedEntrance={selectedCard.suggestedEntrance} size="md" />
                    </div>
                  </div>
                </div>
              )}

              {/* Entry Guidance fallback */}
              {!selectedCard.suggestedEntrance && selectedCard.entryGuidance && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px' }}>
                  <span style={{ fontSize: '1rem', flexShrink: 0 }}>🚪</span>
                  <span style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: 1.4 }}>{selectedCard.entryGuidance}</span>
                </div>
              )}

              {/* Show Map */}
              {selectedCard.gridAddress && (
                <div style={{ marginTop: '14px', textAlign: 'center' }}>
                  <button
                    onClick={() => setShowMap(!showMap)}
                    style={{ background: 'none', border: `1px solid ${showMap ? '#1a6b3c' : '#d1d5db'}`, borderRadius: '8px', padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, color: showMap ? '#1a6b3c' : '#374151', cursor: 'pointer' }}
                    aria-expanded={showMap}
                    aria-label="Show venue map with seat"
                  >
                    {showMap ? '🗺️ Hide Map' : '🗺️ Show Seat on Map'}
                  </button>
                  {showMap && (
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                      <MiniFloorplanMap highlightedGridAddress={selectedCard.gridAddress} suggestedEntrance={selectedCard.suggestedEntrance} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If loading a guest card, show spinner
  if (cardLoading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: TEXT_MUTED }}>
          Loading guest info...
        </div>
      </div>
    );
  }

  // If error loading card, show error with back button
  if (cardError) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#d32f2f', marginBottom: '12px' }}>{cardError}</p>
          <button onClick={handleBackToResults} style={{ background: 'none', border: 'none', color: PRIMARY, fontWeight: 600, cursor: 'pointer' }}>← Back to results</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.brandMain}>Kotoku Mobile</div>
        <div style={styles.brandSub}>Powered by KDUHUB.COM</div>
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ ...styles.modeBadge, backgroundColor: modeColor }}>{modeLabel}</span>
          {cachedCount > 0 && (
            <span style={{ ...styles.modeBadge, backgroundColor: 'rgba(255,255,255,0.2)' }}>
              {cachedCount} cached
            </span>
          )}
        </div>
      </div>

      {/* Sync & Mode Controls */}
      <div style={styles.controlBar}>
        <button
          style={{ ...styles.syncBtn, opacity: syncing ? 0.6 : 1 }}
          onClick={handleSync}
          disabled={syncing || !isOnline}
        >
          {syncing ? 'Syncing...' : '⟳ Sync Data'}
        </button>
        <button
          style={{ ...styles.offlineBtn, backgroundColor: forceOffline ? '#e65100' : '#6b7280' }}
          onClick={toggleForceOffline}
        >
          {forceOffline ? '⚡ Online Mode' : '✈️ Force Offline'}
        </button>
      </div>
      {cacheTime && (
        <div style={styles.cacheInfo}>Last synced: {cacheTime}</div>
      )}
      {syncError && (
        <div style={{ ...styles.cacheInfo, color: '#d32f2f' }}>{syncError}</div>
      )}
      {useOffline && cachedCount === 0 && (
        <div style={styles.warning}>No cached data. Connect to WiFi and tap "Sync Data" first.</div>
      )}

      {/* Instructions (collapsible) */}
      {!searched && (
        <div style={{ margin: '0 16px 8px', padding: '10px 12px', backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', fontSize: '0.75rem', color: TEXT_MUTED }}>
          <div style={{ fontWeight: 700, marginBottom: '4px', color: TEXT_DARK, fontSize: '0.8rem' }}>How to use:</div>
          <div>1. <strong>Sync</strong> — Tap "Sync Data" on WiFi before the event</div>
          <div>2. <strong>Search</strong> — Type guest name, ticket #, or order ID</div>
          <div>3. <strong>Verify</strong> — Confirm table, seat, and zone</div>
          <div>4. <strong>Check In</strong> — Tap the green button (online only)</div>
          <div>5. <strong>Direct</strong> — Use the 📍 directions to guide the guest</div>
          <div style={{ marginTop: '6px', fontStyle: 'italic' }}>Offline mode uses cached data. Check-in requires online.</div>
        </div>
      )}

      {/* Search area */}
      <div style={styles.searchArea}>
        <div style={styles.searchRow}>
          <input
            type="text"
            style={styles.input}
            placeholder="Name, ticket #, order, or table..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search guests"
          />
          <button
            style={styles.searchBtn}
            onClick={handleSearch}
            disabled={loading || query.trim().length < 2}
            aria-label="Search"
          >
            {loading ? '...' : '🔍'}
          </button>
          <label
            style={{ ...styles.searchBtn, backgroundColor: '#6b4c9a', position: 'relative' }}
            aria-label="Scan QR code"
          >
            📷
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleScanFile}
              style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', top: 0, left: 0, cursor: 'pointer' }}
            />
          </label>
        </div>
        {scanning && <div style={{ textAlign: 'center', fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '6px' }}>Reading QR code...</div>}
        {scanError && <div style={{ color: '#d32f2f', fontSize: '0.78rem', textAlign: 'center', marginTop: '6px' }}>{scanError}</div>}
      </div>

      {/* Hidden div for html5-qrcode file scanning */}
      <div id="kotoku-qr-file-reader" style={{ display: 'none' }} />

      {/* Results area */}
      <div style={styles.resultsArea}>
        {!searched && (
          <div style={styles.emptyState}>
            Type a name, ticket #, order ID, or table name
          </div>
        )}

        {searched && !loading && results.length === 0 && (
          <div style={styles.emptyState}>No guests found</div>
        )}

        {searched && results.length > 0 && (
          <div style={styles.resultCount}>{results.length} result{results.length !== 1 ? 's' : ''} for "{query.trim()}"</div>
        )}

        {results.map((guest, idx) => (
          <div key={idx} style={styles.card}>
            <div
              style={styles.cardNameRow}
              onClick={() => handleGuestClick(guest)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleGuestClick(guest); }}
              aria-label={`View details for ${guest.fullName}`}
            >
              <span style={styles.cardName}>{guest.fullName}</span>
              <span style={styles.detailPill}>View Details</span>
            </div>
            <div style={styles.cardGrid}>
              <div style={styles.cardField}>
                <span style={styles.fieldLabel}>Table</span>
                <span style={{ ...styles.fieldValue, color: guest.tableName ? '#166534' : TEXT_MUTED }}>
                  {guest.tableName || (guest.tableNumber != null ? `#${guest.tableNumber}` : '—')}
                </span>
              </div>
              <div style={styles.cardField}>
                <span style={styles.fieldLabel}>Seat</span>
                <span style={styles.fieldValue}>
                  {guest.seatNumber != null ? guest.seatNumber : '—'}
                </span>
              </div>
              <div style={styles.cardField}>
                <span style={styles.fieldLabel}>Ticket</span>
                <span style={styles.fieldValue}>
                  {guest.ticketNumber || '—'}
                </span>
              </div>
              <div style={styles.cardField}>
                <span style={styles.fieldLabel}>Order</span>
                <span style={styles.fieldValue}>
                  {guest.orderReferenceId || '—'}
                </span>
              </div>
              <div style={styles.cardField}>
                <span style={styles.fieldLabel}>Chapter</span>
                <span style={styles.fieldValue}>
                  {guest.chapterAffiliation || '—'}
                </span>
              </div>
              <div style={styles.cardField}>
                <span style={styles.fieldLabel}>Zone</span>
                <span style={styles.fieldValue}>
                  {guest.zoneName || '—'}
                </span>
              </div>
            </div>
            {/* Check In status & directions (both modes) */}
            {guest.registrationId && (
              <div style={{ marginTop: '12px', borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '10px' }}>
                {/* Directions */}
                {guest.directions && (
                  <div style={{ fontSize: '0.78rem', color: '#6b4c9a', marginBottom: '8px', fontWeight: 500 }}>
                    📍 {guest.directions}
                  </div>
                )}

                {/* Already checked in */}
                {guest.isCheckedIn ? (
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#166534', backgroundColor: '#dcfce7', padding: '8px 12px', borderRadius: '8px', textAlign: 'center' }}>
                      ✓ Checked in{guest.pickupLocation ? ` at ${guest.pickupLocation.replace(/_/g, ' ')}` : ''}
                    </div>
                    {!useOffline && (
                      <button
                        style={styles.undoBtn}
                        onClick={() => handleUndoCheckIn(guest.registrationId!)}
                        disabled={checkInStatus[guest.registrationId!]?.loading}
                      >
                        {checkInStatus[guest.registrationId!]?.loading ? 'Reversing...' : '↩ Undo Check-In'}
                      </button>
                    )}
                  </div>
                ) : checkInStatus[guest.registrationId]?.message ? (
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: checkInStatus[guest.registrationId].message!.includes('✓') || checkInStatus[guest.registrationId].message!.includes('already') ? '#166534' : checkInStatus[guest.registrationId].message!.includes('↩') ? '#1565c0' : '#d32f2f', backgroundColor: checkInStatus[guest.registrationId].message!.includes('✓') || checkInStatus[guest.registrationId].message!.includes('already') ? '#dcfce7' : checkInStatus[guest.registrationId].message!.includes('↩') ? '#eff6ff' : '#fef2f2', padding: '8px 12px', borderRadius: '8px', textAlign: 'center' }}>
                      {checkInStatus[guest.registrationId].message}
                    </div>
                    {!useOffline && (checkInStatus[guest.registrationId].message!.includes('✓') || checkInStatus[guest.registrationId].message!.includes('already')) && (
                      <button
                        style={styles.undoBtn}
                        onClick={() => handleUndoCheckIn(guest.registrationId!)}
                        disabled={checkInStatus[guest.registrationId!]?.loading}
                      >
                        {checkInStatus[guest.registrationId!]?.loading ? 'Reversing...' : '↩ Undo Check-In'}
                      </button>
                    )}
                  </div>
                ) : !useOffline ? (
                  <button
                    style={styles.checkInBtn}
                    onClick={() => handleCheckIn(guest.registrationId!)}
                    disabled={checkInStatus[guest.registrationId]?.loading}
                  >
                    {checkInStatus[guest.registrationId]?.loading ? 'Checking in...' : '✓ Check In'}
                  </button>
                ) : (
                  <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center', fontStyle: 'italic' }}>
                    Not checked in (go online to check in)
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── QR Code Display Helper ─── */

function QrCodeDisplay({ registrationId }: { registrationId: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && registrationId) {
      const payload = JSON.stringify({ type: 'guest', identifier: registrationId });
      QRCode.toCanvas(canvasRef.current, payload, { width: 160, margin: 2, color: { dark: '#000', light: '#fff' } }).catch(() => {});
    }
  }, [registrationId]);

  return <canvas ref={canvasRef} style={{ borderRadius: '8px' }} />;
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: BG_PAGE,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: `linear-gradient(135deg, ${PRIMARY}, #0f4a28)`,
    padding: '28px 20px 20px',
    textAlign: 'center',
  },
  brandMain: {
    fontFamily: FONT_HEADING,
    fontSize: '1.8rem',
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '0.5px',
  },
  brandSub: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.7)',
    marginTop: '2px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  modeBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '0.68rem',
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '0.5px',
  },
  controlBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    padding: '12px 16px 4px',
    flexWrap: 'wrap',
  },
  syncBtn: {
    padding: '8px 16px',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    minHeight: '36px',
  },
  offlineBtn: {
    padding: '8px 16px',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    minHeight: '36px',
  },
  cacheInfo: {
    textAlign: 'center',
    fontSize: '0.72rem',
    color: TEXT_MUTED,
    padding: '4px 16px',
  },
  warning: {
    textAlign: 'center',
    fontSize: '0.82rem',
    color: '#e65100',
    backgroundColor: '#fff3e0',
    padding: '8px 16px',
    margin: '8px 16px',
    borderRadius: '8px',
  },
  searchArea: {
    padding: '12px 16px 0',
    backgroundColor: BG_PAGE,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  searchRow: {
    display: 'flex',
    gap: '10px',
    maxWidth: '600px',
    margin: '0 auto',
  },
  input: {
    flex: 1,
    padding: '14px 16px',
    fontSize: '16px',
    border: `2px solid ${BORDER_LIGHT}`,
    borderRadius: '12px',
    outline: 'none',
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    minHeight: '48px',
  },
  searchBtn: {
    padding: '14px 20px',
    fontSize: '1.2rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    minWidth: '56px',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resultsArea: {
    padding: '12px 16px',
    maxWidth: '600px',
    margin: '0 auto',
    width: '100%',
  },
  resultCount: {
    fontSize: '0.78rem',
    color: TEXT_MUTED,
    marginBottom: '8px',
  },
  emptyState: {
    textAlign: 'center',
    color: TEXT_MUTED,
    fontSize: '0.95rem',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '16px',
    marginBottom: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardName: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  cardNameRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    cursor: 'pointer',
    padding: '4px 0',
  },
  detailPill: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    backgroundColor: '#1565c0',
    color: '#fff',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '8px',
  },
  cardField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  fieldLabel: {
    fontSize: '0.65rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  fieldValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  checkInBtn: {
    width: '100%',
    padding: '10px',
    fontSize: '0.9rem',
    fontWeight: 700,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  undoBtn: {
    width: '100%',
    padding: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '6px',
  },
};
